import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  Close,
  Assessment,
  PictureAsPdf,
  TableChart,
  Article,
  CalendarToday,
  CheckBox,
  CheckBoxOutlineBlank,
  KeyboardArrowDown,
  KeyboardArrowUp,
  FileDownload,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReportFormatsQuery,
  useAddReportFormatMutation,
  useUpdateReportFormatMutation,
  useDeleteReportFormatMutation,
  ReportFormat,
  AddReportFormatRequest,
  REPORT_TYPE_OPTIONS,
  DATE_RANGE_OPTIONS,
  EXPORT_TYPE_OPTIONS,
  AVAILABLE_FIELDS,
} from '../../features/admin/reportListApi.ts';

// ─── Form State ───────────────────────────────────────────────────────────────

interface FormData {
  name: string;
  report_type: string;
  fields: string[];
  date_range: string;
  export_type: string;
  status: number;
}

interface FormErrors {
  name?: string;
  report_type?: string;
  fields?: string;
}

const defaultForm: FormData = {
  name: '',
  report_type: '',
  fields: [],
  date_range: 'This Month',
  export_type: 'PDF',
  status: 1,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getExportIcon = (type: string) => {
  if (type === 'PDF')   return <PictureAsPdf sx={{ fontSize: 14 }} />;
  if (type === 'Excel') return <TableChart   sx={{ fontSize: 14 }} />;
  return <Article sx={{ fontSize: 14 }} />;
};

const getExportColor = (type: string) => {
  if (type === 'PDF')   return { bgcolor: '#ffebee', color: '#c62828' };
  if (type === 'Excel') return { bgcolor: '#e8f5e9', color: '#2e7d32' };
  return { bgcolor: '#e3f2fd', color: '#1565c0' };
};

const getReportTypeColor = (type: string) => {
  const colors: Record<string, { bgcolor: string; color: string }> = {
    'Sales Report':     { bgcolor: '#e8f5e9', color: '#2e7d32' },
    'Order Report':     { bgcolor: '#e3f2fd', color: '#1565c0' },
    'User Report':      { bgcolor: '#f3e5f5', color: '#7b1fa2' },
    'Inventory Report': { bgcolor: '#fff3e0', color: '#e65100' },
    'Payment Report':   { bgcolor: '#fce4ec', color: '#c2185b' },
    'Credit Report':    { bgcolor: '#e0f2f1', color: '#00695c' },
    'Wallet Report':    { bgcolor: '#ede7f6', color: '#4527a0' },
    'Return Report':    { bgcolor: '#fff8e1', color: '#f57f17' },
  };
  return colors[type] || { bgcolor: '#f1f2f4', color: '#6d7175' };
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const ReportsManagement = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,      setMobileOpen]      = useState(false);
  const [page,            setPage]            = useState(1);
  const [searchStr,       setSearchStr]       = useState('');
  const [tabValue,        setTabValue]        = useState(0);
  const [expandedId,      setExpandedId]      = useState<number | null>(null);
  const [snackbar,        setSnackbar]        = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Dialog
  const [formOpen,        setFormOpen]        = useState(false);
  const [editingFormat,   setEditingFormat]   = useState<ReportFormat | null>(null);
  const [formData,        setFormData]        = useState<FormData>(defaultForm);
  const [formErrors,      setFormErrors]      = useState<FormErrors>({});
  const [isSubmitting,    setIsSubmitting]    = useState(false);
  const [deletingId,      setDeletingId]      = useState<number | null>(null);

  // API hooks
  const { data: listResponse, isLoading, isError, refetch } = useListReportFormatsQuery();
  const [addFormat]    = useAddReportFormatMutation();
  const [updateFormat] = useUpdateReportFormatMutation();
  const [deleteFormat, { isLoading: isDeleting }] = useDeleteReportFormatMutation();

  const allFormats: ReportFormat[] = listResponse?.data || [];

  const usersPerPage = 10;

  // ── Filtered + paginated ──
  const filteredFormats = useMemo(() =>
    allFormats.filter((f) => {
      if (!searchStr) return true;
      const sl = searchStr.toLowerCase();
      return (
        f.format_name?.toLowerCase().includes(sl) ||
        f.report_type?.toLowerCase().includes(sl) ||
        f.export_type?.toLowerCase().includes(sl)
      );
    }),
  [allFormats, searchStr]);

  const totalPages     = Math.ceil(filteredFormats.length / usersPerPage);
  const paginatedList  = filteredFormats.slice((page - 1) * usersPerPage, page * usersPerPage);

  // Stats
  const activeCount  = allFormats.filter(f => Number(f.status) === 1).length;
  const pdfCount     = allFormats.filter(f => f.export_type === 'PDF').length;
  const excelCount   = allFormats.filter(f => f.export_type === 'Excel').length;

  // ── Handlers ──
  const handleDrawerToggle  = () => setMobileOpen(v => !v);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const clearFilters        = () => { setSearchStr(''); setPage(1); };
  const handleToggleExpand  = (id: number) => setExpandedId(v => v === id ? null : id);

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  // ── Form helpers ──
  const resetForm    = () => { setFormData(defaultForm); setFormErrors({}); setEditingFormat(null); };
  const handleAdd    = () => { resetForm(); setFormOpen(true); };
  const handleClose  = () => { setFormOpen(false); resetForm(); };

  const handleEdit = (fmt: ReportFormat) => {
    setFormData({
      name:        fmt.format_name,
      report_type: fmt.report_type,
      fields:      Array.isArray(fmt.fields) ? fmt.fields : [],
      date_range:  fmt.date_range  || 'This Month',
      export_type: fmt.export_type || 'PDF',
      status:      Number(fmt.status) ?? 1,
    });
    setEditingFormat(fmt);
    setFormOpen(true);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // reset fields when report_type changes
      if (name === 'report_type') next.fields = [];
      return next;
    });
    if (formErrors[name as keyof FormErrors]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleField = (field: string) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field],
    }));
    if (formErrors.fields) setFormErrors(prev => ({ ...prev, fields: '' }));
  };

  const selectAllFields = () => {
    const available = AVAILABLE_FIELDS[formData.report_type] || [];
    setFormData(prev => ({ ...prev, fields: [...available] }));
  };

  const clearAllFields = () => setFormData(prev => ({ ...prev, fields: [] }));

  const validateForm = (): boolean => {
    const err: FormErrors = {};
    if (!formData.name.trim())    err.name        = 'Format name is required';
    if (!formData.report_type)    err.report_type = 'Report type is required';
    if (!formData.fields.length)  err.fields      = 'Select at least one field';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name:        formData.name.trim(),
        report_type: formData.report_type,
        fields:      formData.fields,
        date_range:  formData.date_range,
        export_type: formData.export_type,
        status:      formData.status,
      };
      const result = await (editingFormat
        ? updateFormat({ id: editingFormat.id, ...payload })
        : addFormat(payload)
      ).unwrap();
      setSnackbar({ open: true, message: result.message || `Format ${editingFormat ? 'updated' : 'created'} successfully!`, severity: 'success' });
      handleClose();
      refetch();
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.data?.message || error.message || 'Error saving format.', severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  const handleDelete = async (fmt: ReportFormat) => {
    if (!window.confirm(`Delete report format "${fmt.format_name}"?`)) return;
    try {
      setDeletingId(fmt.id);
      const result = await deleteFormat({ id: fmt.id }).unwrap();
      setSnackbar({ open: true, message: result.message || 'Deleted!', severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Error deleting.', severity: 'error' });
    } finally { setDeletingId(null); }
  };

  const availableFields = AVAILABLE_FIELDS[formData.report_type] || [];

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Report Formats</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
                  sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000000' } }}>
                  Add Format
                </Button>
              </Box>
            </Box>

            {/* Stats */}
            {!isLoading && !isError && allFormats.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Assessment sx={{ color: '#1565c0', fontSize: 24 }} />,    bg: '#e3f2fd', value: allFormats.length, label: 'Total Formats',  isCount: true },
                  { icon: <CheckBox    sx={{ color: '#2e7d32', fontSize: 24 }} />,    bg: '#e8f5e9', value: activeCount,       label: 'Active Formats', isCount: true },
                  { icon: <PictureAsPdf sx={{ color: '#c62828', fontSize: 24 }} />,  bg: '#ffebee', value: pdfCount,           label: 'PDF Formats',   isCount: true },
                  { icon: <TableChart  sx={{ color: '#e65100', fontSize: 24 }} />,   bg: '#fff3e0', value: excelCount,         label: 'Excel Formats', isCount: true },
                ].map(({ icon, bg, value, label }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Table Card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Formats (${filteredFormats.length})`} />
                </Tabs>
              </Box>

              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Search by name, type, or export format..." value={searchStr} onChange={handleSearch} fullWidth
                  sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Clear
                </Button>
              </Box>

              {/* Loading / Error / Empty */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load report formats. Please try again.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedList.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No report formats found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first report format'}
                  </Typography>
                  {!searchStr && (
                    <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
                      sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500 }}>
                      Add First Format
                    </Button>
                  )}
                </Box>
              )}

              {/* Table */}
              {!isLoading && paginatedList.length > 0 && (
                <Box sx={{ bgcolor: 'white' }}>
                  {/* Desktop Header */}
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 60px 1fr 160px 120px 120px 80px 80px 100px', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'Format Name', 'Report Type', 'Date Range', 'Export', 'Fields', 'Status', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedList.map((fmt) => {
                    const isThisDeleting = deletingId === fmt.id;
                    const fieldArr = Array.isArray(fmt.fields) ? fmt.fields : [];
                    const isActive = Number(fmt.status) === 1;

                    return (
                      <Box key={fmt.id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid', gridTemplateColumns: '48px 60px 1fr 160px 120px 120px 80px 80px 100px',
                            gap: 1.5, px: 2, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', cursor: 'pointer',
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(fmt.id)}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(fmt.id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedId === fmt.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{fmt.id}</Typography>

                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{fmt.format_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatDate(fmt.created_at)}</Typography>
                              </Box>

                              <Chip label={fmt.report_type} size="small"
                                sx={{ ...getReportTypeColor(fmt.report_type), fontWeight: 600, fontSize: '11px', height: '24px', maxWidth: '150px' }} />

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 12, color: '#6d7175' }} />
                                <Typography variant="caption" color="text.secondary">{fmt.date_range}</Typography>
                              </Box>

                              <Chip
                                icon={getExportIcon(fmt.export_type)}
                                label={fmt.export_type}
                                size="small"
                                sx={{ ...getExportColor(fmt.export_type), fontWeight: 600, fontSize: '11px', height: '24px' }}
                              />

                              <Chip label={`${fieldArr.length} fields`} size="small"
                                sx={{ bgcolor: '#f1f2f4', color: '#6d7175', fontWeight: 600, fontSize: '11px', height: '24px' }} />

                              <Chip
                                label={isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  bgcolor: isActive ? '#d1f4e0' : '#f1f2f4',
                                  color:   isActive ? '#00783e' : '#6d7175',
                                  fontWeight: 600, fontSize: '11px', height: '24px',
                                }}
                              />

                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton onClick={e => { e.stopPropagation(); handleEdit(fmt); }} size="small" disabled={isThisDeleting} sx={{ color: '#202223' }}><Edit fontSize="small" /></IconButton>
                                <IconButton onClick={e => { e.stopPropagation(); handleDelete(fmt); }} disabled={isThisDeleting || isDeleting} size="small" sx={{ color: '#d32f2f' }}>
                                  {isThisDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            /* Mobile Card */
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>{fmt.format_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">ID: #{fmt.id}</Typography>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(fmt.id); }} sx={{ color: '#6d7175' }}>
                                  {expandedId === fmt.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                <Chip label={fmt.report_type} size="small" sx={{ ...getReportTypeColor(fmt.report_type), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                <Chip icon={getExportIcon(fmt.export_type)} label={fmt.export_type} size="small" sx={{ ...getExportColor(fmt.export_type), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                <Chip label={isActive ? 'Active' : 'Inactive'} size="small"
                                  sx={{ bgcolor: isActive ? '#d1f4e0' : '#f1f2f4', color: isActive ? '#00783e' : '#6d7175', fontWeight: 600, fontSize: '11px', height: '22px' }} />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Details */}
                        <Collapse in={expandedId === fmt.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Format Details</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" startIcon={<Edit />} onClick={() => handleEdit(fmt)} disabled={isThisDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Edit</Button>
                                <Button size="small" color="error" startIcon={isThisDeleting ? <CircularProgress size={16} /> : <Delete />}
                                  onClick={() => handleDelete(fmt)} disabled={isThisDeleting || isDeleting}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500 }}>Delete</Button>
                              </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {[
                                { label: 'Report Type',  value: fmt.report_type,  chip: true, chipSx: getReportTypeColor(fmt.report_type) },
                                { label: 'Date Range',   value: fmt.date_range,   chip: false },
                                { label: 'Export Type',  value: fmt.export_type,  chip: true, chipSx: getExportColor(fmt.export_type) },
                                { label: 'Status',       value: isActive ? 'Active' : 'Inactive', chip: true, chipSx: isActive ? { bgcolor: '#d1f4e0', color: '#00783e' } : { bgcolor: '#f1f2f4', color: '#6d7175' } },
                              ].map(({ label, value, chip, chipSx }) => (
                                <Grid item xs={6} sm={3} key={label}>
                                  <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', height: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
                                    {chip && chipSx
                                      ? <Chip label={value} size="small" sx={{ ...(chipSx as any), fontWeight: 600, fontSize: '11px', height: '22px' }} />
                                      : <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{value}</Typography>
                                    }
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Fields */}
                            <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Included Fields</Typography>
                                <Chip label={fieldArr.length} size="small" sx={{ bgcolor: '#f1f2f4', color: '#6d7175', fontWeight: 600, height: '20px', fontSize: '11px' }} />
                              </Box>
                              {fieldArr.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No fields selected</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                  {fieldArr.map((field) => (
                                    <Chip key={field} label={field.replace(/_/g, ' ')} size="small"
                                      sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500, fontSize: '11px', height: '24px', textTransform: 'capitalize' }} />
                                  ))}
                                </Box>
                              )}
                            </Card>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{editingFormat ? 'Edit Report Format' : 'Add Report Format'}</Typography>
            <IconButton onClick={handleClose} size="small" disabled={isSubmitting}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              {/* Name */}
              <TextField name="name" label="Format Name *" value={formData.name} onChange={handleTextChange}
                error={!!formErrors.name} helperText={formErrors.name} fullWidth required disabled={isSubmitting}
                placeholder="e.g. Monthly Sales Summary" />

              {/* Report Type */}
              <FormControl fullWidth error={!!formErrors.report_type} disabled={isSubmitting}>
                <InputLabel>Report Type *</InputLabel>
                <Select name="report_type" value={formData.report_type} onChange={handleSelectChange} label="Report Type *">
                  {REPORT_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formErrors.report_type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{formErrors.report_type}</Typography>}
              </FormControl>

              {/* Fields */}
              {formData.report_type && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: formErrors.fields ? '#d32f2f' : '#202223' }}>
                      Select Fields * {formData.fields.length > 0 && `(${formData.fields.length} selected)`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" onClick={selectAllFields} sx={{ textTransform: 'none', fontSize: '12px', color: '#1565c0' }}>Select All</Button>
                      <Button size="small" onClick={clearAllFields} sx={{ textTransform: 'none', fontSize: '12px', color: '#6d7175' }}>Clear</Button>
                    </Box>
                  </Box>
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: '8px', borderColor: formErrors.fields ? '#d32f2f' : '#e1e3e5' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                      {availableFields.map((field) => {
                        const selected = formData.fields.includes(field);
                        return (
                          <Chip
                            key={field}
                            label={field.replace(/_/g, ' ')}
                            size="small"
                            icon={selected ? <CheckBox sx={{ fontSize: '14px !important' }} /> : <CheckBoxOutlineBlank sx={{ fontSize: '14px !important' }} />}
                            onClick={() => toggleField(field)}
                            sx={{
                              cursor: 'pointer',
                              textTransform: 'capitalize',
                              fontWeight: 500,
                              fontSize: '12px',
                              height: '28px',
                              bgcolor: selected ? '#202223' : '#f1f2f4',
                              color:   selected ? 'white'    : '#6d7175',
                              '& .MuiChip-icon': { color: selected ? 'white' : '#6d7175' },
                              '&:hover': { bgcolor: selected ? '#000' : '#e1e3e5' },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Card>
                  {formErrors.fields && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5, display: 'block' }}>{formErrors.fields}</Typography>}
                </Box>
              )}

              {/* Date Range + Export Type in row */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth disabled={isSubmitting}>
                    <InputLabel>Date Range</InputLabel>
                    <Select name="date_range" value={formData.date_range} onChange={handleSelectChange} label="Date Range">
                      {DATE_RANGE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth disabled={isSubmitting}>
                    <InputLabel>Export Type</InputLabel>
                    <Select name="export_type" value={formData.export_type} onChange={handleSelectChange} label="Export Type">
                      {EXPORT_TYPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#fafbfb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>Status</Typography>
                  <Typography variant="caption" color="text.secondary">Enable or disable this report format</Typography>
                </Box>
                <Switch
                  checked={formData.status === 1}
                  onChange={e => setFormData(prev => ({ ...prev, status: e.target.checked ? 1 : 0 }))}
                  disabled={isSubmitting}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#202223' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiTrack': { bgcolor: '#202223' } }}
                />
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#f44336', color: '#eeeeee', fontWeight: 500 }}>
              Close
            </Button>
            <Button onClick={handleClose} disabled={isSubmitting} variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#F78E12', color: '#eeeeee', fontWeight: 500 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained"
              disabled={isSubmitting || !formData.name || !formData.report_type || !formData.fields.length}
              sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000000' } }}>
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editingFormat ? 'Updating...' : 'Creating...'}
                </Box>
              ) : (editingFormat ? 'Update Format' : 'Create Format')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsManagement;